import axios from 'axios';
import { api } from '@/api/http';
import { socket } from '@/api/socket';
import { formatApiError } from '@/lib/format-api-error';
import { isSenderApp } from '@/lib/app-mode';
import { isDevMode } from '@/lib/is-dev-mode';
import { isSessionEndMessage } from '@/lib/session-messages';

const DEFAULT_REFRESH_BEFORE_SEC = 3600;
const RETRY_BACKOFF_SEC = [60, 120, 300] as const;
const MIN_REFRESH_DELAY_SEC = 60;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshPromise: Promise<number | null> | null = null;
let cachedRefreshBeforeSec = DEFAULT_REFRESH_BEFORE_SEC;
let lastKnownExpiresIn: number | null = null;
let retryAttempt = 0;

function clearRefreshTimer() {
	if (refreshTimer !== null) {
		clearTimeout(refreshTimer);
		refreshTimer = null;
	}
}

function reconnectSocketIfNeeded() {
	if (isDevMode || isSenderApp || !socket) return;
	if (socket.connected) {
		socket.disconnect();
	}
	socket.connect();
}

export function computeRefreshDelaySec(sessionExpiresIn: number, refreshBeforeSec: number): number {
	return Math.max(sessionExpiresIn - refreshBeforeSec, MIN_REFRESH_DELAY_SEC);
}

function scheduleRefreshTimer(delaySec: number, callback: () => void) {
	clearRefreshTimer();
	refreshTimer = setTimeout(callback, delaySec * 1000);
}

function scheduleRetryRefresh() {
	if (lastKnownExpiresIn === null || lastKnownExpiresIn <= MIN_REFRESH_DELAY_SEC) {
		return;
	}

	const delaySec = RETRY_BACKOFF_SEC[Math.min(retryAttempt, RETRY_BACKOFF_SEC.length - 1)];
	retryAttempt += 1;
	scheduleRefreshTimer(delaySec, () => {
		void refreshSession();
	});
}

export function clearSessionKeeper() {
	clearRefreshTimer();
	refreshPromise = null;
	lastKnownExpiresIn = null;
	retryAttempt = 0;
}

export function scheduleSessionRefresh(sessionExpiresIn: number, refreshBeforeSec = cachedRefreshBeforeSec) {
	lastKnownExpiresIn = sessionExpiresIn;
	const delaySec = computeRefreshDelaySec(sessionExpiresIn, refreshBeforeSec);
	scheduleRefreshTimer(delaySec, () => {
		void refreshSession();
	});
}

function shouldStopRefreshOnError(error: unknown): boolean {
	if (!axios.isAxiosError(error) || error.response?.status !== 403) {
		return false;
	}
	const message = formatApiError(error.response.data);
	return isSessionEndMessage(message);
}

export async function refreshSession(): Promise<number | null> {
	if (refreshPromise) {
		return refreshPromise;
	}

	refreshPromise = (async () => {
		try {
			const res = await api.get('/me', { suppressToast: true, suppressConnectionOverlay: true });
			const sessionExpiresIn = res.data?.sessionExpiresIn;
			const sessionRefreshBeforeSec = res.data?.sessionRefreshBeforeSec;
			if (typeof sessionRefreshBeforeSec === 'number' && sessionRefreshBeforeSec > 0) {
				cachedRefreshBeforeSec = sessionRefreshBeforeSec;
			}
			if (typeof sessionExpiresIn === 'number' && sessionExpiresIn > 0) {
				retryAttempt = 0;
				scheduleSessionRefresh(sessionExpiresIn, cachedRefreshBeforeSec);
				reconnectSocketIfNeeded();
				return sessionExpiresIn;
			}
			return null;
		} catch (error) {
			console.warn('[session-keeper] refresh failed', error);
			if (!shouldStopRefreshOnError(error)) {
				scheduleRetryRefresh();
			}
			return null;
		} finally {
			refreshPromise = null;
		}
	})();

	return refreshPromise;
}

export function startSessionKeeper(sessionExpiresIn: number, sessionRefreshBeforeSec?: number) {
	if (typeof sessionRefreshBeforeSec === 'number' && sessionRefreshBeforeSec > 0) {
		cachedRefreshBeforeSec = sessionRefreshBeforeSec;
	}
	retryAttempt = 0;
	scheduleSessionRefresh(sessionExpiresIn, cachedRefreshBeforeSec);
}
